import { Box, Button, createTheme, Dialog, DialogActions, FormControl, InputAdornment, InputLabel, MenuItem, Select, TextField, ThemeProvider } from '@mui/material';
import { CURRENCY_LABEL_FORMATS, getCurrencyOptions } from 'modules/enumerations/enumerationHelpers';
import { DialogTitle, Typography, withStyles } from '@material-ui/core';

import { withRouter } from 'react-router-dom';

import React, { useEffect, useState } from 'react';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';
import { showToast } from 'modules/layout/layout.actions';
import Spinner from 'modules/common/spinner.component';
import { COLOR_PRIMARY, COLOR_SECONDARY, COLOR_WARNING } from 'styles/theme';
import { createExpense, getExpenseSummaries } from '../store/expenses.actions';
import { GET_EXPENSE_SUMMARIES_FAILURE, POST_EXPENSE_SUMMARY_SUCCESS } from '../store/types';
import { ZERO_GUID } from 'modules/enumerations/enumerationIds';

const styles = theme => ({
  wideControl: {
    minWidth: 174
  },
  section: {
    marginBottom: theme.spacing(2)
  }
});

const CreateExpenseDialog = props => {
  const { reimbursementTypes, currencies, expensePaidTos, authorizationSummaries, vendors } = props;

  const currencyOptions = getCurrencyOptions(currencies, CURRENCY_LABEL_FORMATS.LONG);

  const theme = createTheme({
    palette: {
      primary: COLOR_PRIMARY,
      secondary: COLOR_SECONDARY,
      warning: COLOR_WARNING
    }
  });

  const [isLoadingModal, setIsLoadingModal] = useState(false);
  const [isSubmittable, setIsSubmittable] = useState(false);

  // Main expense fields
  const [description, setDescription] = useState('');
  const [total, setTotal] = useState(0);
  const [reimbursementTypeId, setReimbursementTypeId] = useState(ZERO_GUID);
  const [payInCurrencyId, setPayInCurrencyId] = useState(ZERO_GUID);
  const [equivalentCurrencyId, setEquivalentCurrencyId] = useState(ZERO_GUID);
  const [expensePaidToId, setExpensePaidToId] = useState(ZERO_GUID);
  const [vendorId, setVendorId] = useState(null);
  const [authorizationId, setAuthorizationId] = useState(null);

  const createExpense = async () => {
    setIsLoadingModal(true);

    const expenseInfo = {
      addressInformation: undefined,
      bankingInformation: undefined,
      payee: {
        payeeId: undefined,
        payeeTypeId: expensePaidToId === 'e46870a2-7b13-4ea6-8f1f-bbe35a5f8f8c' ? '38c8b61e-2a18-4e3f-9c59-3a5e9b1a1d2a' : 'c3b2c3c1-0324-4c7b-8b97-ef3f3a4a3f6e', // Vendor or Authorization
        vendorId: expensePaidToId === 'e46870a2-7b13-4ea6-8f1f-bbe35a5f8f8c' ? vendorId : null,
        authorizationId: expensePaidToId !== 'e46870a2-7b13-4ea6-8f1f-bbe35a5f8f8c' ? authorizationId : null,
        transfereePayeeId: undefined, // Should be replaced during backend processing
        vendorPayeeId: undefined // Should be replaced during backend processing
      },
      expenseLineItems: [],
      expenseInformation: {
        expenseId: undefined,
        payeeId: undefined, // PayeeId should be replaced during backend processing
        expenseStatusId: '6f3b939e-1f43-4a8b-a103-dbb3f1d3f4f9', // Not Viewed
        dateSubmitted: new Date(),
        description: description,
        total: total,
        expenseAnalystId: undefined, // TODO: Replace with actual analyst Id
        reimbursementTypeId: reimbursementTypeId,
        payInCurrencyId: payInCurrencyId,
        equivalentCurrencyId: equivalentCurrencyId,
        batchTypeId: ZERO_GUID,
        isNotPayable: false,
        isRushPayment: false,
        isExpenseApproved: false,
        notPayableId: ZERO_GUID,
        expensePaidToId: expensePaidToId,
        addedBy: 1, // TODO: Replace with actual internal user ID
        addedOn: new Date(),
        modifiedBy: 1, // TODO: Replace with actual internal user ID
        modifiedOn: new Date()
      }
    };

    const postExpenseResp = await props.createExpense(expenseInfo);
    if (postExpenseResp.type === POST_EXPENSE_SUMMARY_SUCCESS) {
      const getExpensesResp = await props.getExpenseSummaries();
      if (getExpensesResp.type !== GET_EXPENSE_SUMMARIES_FAILURE) {
        props.showToast('Successfully created new expense!', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
        props.onCancel();
      } else {
        props.showToast('Failed to get expense list, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      }
    } else {
      props.showToast('Failed to create expense, please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    }

    setIsLoadingModal(false);
  }

  const onExpenseCreationCancelled = () => {
    setDescription('');
    setTotal(0);
    setReimbursementTypeId(ZERO_GUID);
    setPayInCurrencyId(ZERO_GUID);
    setEquivalentCurrencyId(ZERO_GUID);
    setExpensePaidToId(ZERO_GUID);
    setVendorId(null)
    setAuthorizationId(null);
    props.onCancel();
  }

  useEffect(() => {
    if (description.length !== 0 && total > 0 && reimbursementTypeId !== ZERO_GUID && payInCurrencyId !== ZERO_GUID
      && equivalentCurrencyId !== ZERO_GUID && expensePaidToId !== ZERO_GUID
    ) {
      setIsSubmittable(true);
    } else {
      setIsSubmittable(false);
    }
  }, [description, total, reimbursementTypeId, payInCurrencyId, equivalentCurrencyId, expensePaidToId]);

  return (
    <ThemeProvider theme={theme}>
      <Dialog open={props.open} fullWidth={false} maxWidth={'lg'}>
        <DialogTitle>New Expense</DialogTitle>
        <Box sx={{ display: 'flex', flexDirection: 'row', padding: '20px', flexWrap: 'wrap', gap: '20px' }}>
          <Box>
            <TextField
              key={'description'}
              label={'Description'}
              name={'description'}
              value={description}
              variant="outlined"
              required={true}
              sx={{ maxWidth: '360px' }}
              type="text"
              onChange={(e) => setDescription(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <Typography variant="caption" display="block" color="primary">
              Required
            </Typography>
          </Box>
          <Box>
            <TextField
              key={'total'}
              label={'Total'}
              name={'total'}
              required={true}
              variant="outlined"
              sx={{ maxWidth: '360px' }}
              value={total}
              onChange={(e) => setTotal(e.target.value)}
              type="number"
              InputLabelProps={{ shrink: true }}
              InputProps={{
                startAdornment: <InputAdornment position="start">$</InputAdornment>
              }}
            />
            <Typography variant="caption" display="block" color="primary">
              Required
            </Typography>
          </Box>
          <Box>
            <FormControl sx={{ marginTop: '6px' }}>
              <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }} id={'vendorTypeId'}>
                Expense Paid To
              </InputLabel>
              <Select
                labelId={'expensePaidToId'}
                key={'expensePaidToId'}
                label={'expensePaidToId'}
                name={'expensePaidToId'}
                required={true}
                disabled={false}
                value={expensePaidToId}
                margin="dense"
                variant="standard"
                sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                onChange={e => setExpensePaidToId(e.target.value)}
              >
                {expensePaidTos.map(opt => (
                  <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }} disabled={false}>
                    {opt.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" display="block" color="primary">
              Required
            </Typography>
          </Box>
          {(expensePaidToId === '6c6631e4-1f0a-4ac1-b414-c3d2b1d88d8e' || expensePaidToId === '4e9eab5a-2a5c-4f33-8eb9-2d7c5d5b7a2f') && (
            <Box>
              <FormControl sx={{ marginTop: '6px' }}>
                <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }} id={'vendorTypeId'}>
                  Auth to Pay
                </InputLabel>
                <Select
                  labelId={'authorizationId'}
                  key={'authorizationId'}
                  label={'authorizationId'}
                  name={'authorizationId'}
                  required={true}
                  disabled={false}
                  value={authorizationId}
                  margin="dense"
                  variant="standard"
                  sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                  onChange={e => setAuthorizationId(e.target.value)}
                >
                  {authorizationSummaries.map(opt => (
                    <MenuItem key={opt.fileId} value={opt.fileId} sx={{ margin: 'dense' }} disabled={false}>
                      {opt.firstName} {opt.lastName} - {opt.fileNumber}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" display="block" color="primary">
                Required
              </Typography>
            </Box>
          )}
          {(expensePaidToId === 'e46870a2-7b13-4ea6-8f1f-bbe35a5f8f8c') && (
            <Box>
              <FormControl sx={{ marginTop: '6px' }}>
                <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }} id={'vendorTypeId'}>
                  Vendor to Pay
                </InputLabel>
                <Select
                  labelId={'vendorId'}
                  key={'vendorId'}
                  label={'vendorId'}
                  name={'vendorId'}
                  required={true}
                  disabled={false}
                  value={vendorId}
                  margin="dense"
                  variant="standard"
                  sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                  onChange={e => setVendorId(e.target.value)}
                >
                  {vendors.map(opt => (
                    <MenuItem key={opt.vendorId} value={opt.vendorId} sx={{ margin: 'dense' }} disabled={false}>
                      {opt.companyName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Typography variant="caption" display="block" color="primary">
                Required
              </Typography>
            </Box>
          )}
          <Box>
            <FormControl sx={{ marginTop: '6px' }}>
              <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }} id={'industryId'}>
                Reimbursement Type
              </InputLabel>
              <Select
                labelId={'reimbursementTypeId'}
                key={'reimbursementTypeId'}
                label={'reimbursementTypeId'}
                name={'reimbursementTypeId'}
                required={true}
                disabled={false}
                value={reimbursementTypeId}
                margin="dense"
                variant="standard"
                sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                onChange={e => setReimbursementTypeId(e.target.value)}
              >
                {reimbursementTypes.map(opt => (
                  <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }} disabled={false}>
                    {opt.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" display="block" color="primary">
              Required
            </Typography>
          </Box>
          <Box>
            <FormControl sx={{ marginTop: '6px' }}>
              <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }} id={'industryId'}>
                Pay In Currency
              </InputLabel>
              <Select
                labelId={'payInCurrencyId'}
                key={'payInCurrencyId'}
                label={'payInCurrencyId'}
                name={'payInCurrencyId'}
                required={true}
                disabled={false}
                value={payInCurrencyId}
                margin="dense"
                variant="standard"
                sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                onChange={e => setPayInCurrencyId(e.target.value)}
              >
                {currencyOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }} disabled={false}>
                    {opt.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" display="block" color="primary">
              Required
            </Typography>
          </Box>
          <Box>
            <FormControl sx={{ marginTop: '6px' }}>
              <InputLabel sx={{ marginLeft: '-15px', marginTop: '7px' }} id={'industryId'}>
                Equivalent Currency
              </InputLabel>
              <Select
                labelId={'equivalentCurrencyId'}
                key={'equivalentCurrencyId'}
                label={'equivalentCurrencyId'}
                name={'equivalentCurrencyId'}
                required={true}
                disabled={false}
                value={equivalentCurrencyId}
                margin="dense"
                variant="standard"
                sx={{ marginTop: '6px', marginBottom: '6px', maxWidth: '360px', minWidth: '180px' }}
                onChange={e => setEquivalentCurrencyId(e.target.value)}
              >
                {currencyOptions.map(opt => (
                  <MenuItem key={opt.id} value={opt.id} sx={{ margin: 'dense' }} disabled={false}>
                    {opt.title}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Typography variant="caption" display="block" color="primary">
              Required
            </Typography>
          </Box>
        </Box>

        <DialogActions sx={{ p: '1.25rem' }}>
          {isLoadingModal && <Spinner />}
          {!isLoadingModal && (
            <>
              <Button onClick={onExpenseCreationCancelled}>Cancel</Button>
              <Button
                onClick={createExpense}
                color="primary"
                type="submit"
                variant="contained"
                disabled={!isSubmittable}
              >
                Create Expense
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};

const mapStateToProps = state => {
  const { reimbursementTypes, currencies, batchTypes, expensePaidTos } = state.enumerations.allEnums;
  const { vendors } = state.common;
  const { authorizationSummaries } = state.authorizations;

  return {
    reimbursementTypes: reimbursementTypes,
    currencies: currencies,
    batchTypes: batchTypes,
    expensePaidTos: expensePaidTos,
    vendors: vendors,
    authorizationSummaries: authorizationSummaries
  };
};

export default compose(
  withStyles(styles),
  withRouter,
  connect(mapStateToProps, {
    createExpense,
    getExpenseSummaries,
    showToast
  }),
)(CreateExpenseDialog);
